import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthMe, Role, PlatformRole, OrgSummary, OrgListResponse } from '@kindred/types';
import { api, ApiClientError } from './apiClient.js';

/**
 * AuthProvider — session/user/role/platformRole + the signed-in user's
 * organisations and the currently-selected one. The org list is fetched from
 * `/org/list` (only while signed in) so the UI can show real merchant names; the
 * selection is persisted to localStorage so it survives a refresh. Also exposes
 * passwordless flow helpers (stubs that call the API). Mirrors the donor
 * `initialised` gate so guards can wait for the first /auth/me.
 */
const SELECTED_ORG_STORAGE_KEY = 'kindred.selectedOrganisationId';

interface AuthState {
  initialised: boolean;
  me: AuthMe | null;
  /** Organisations the signed-in user belongs to (empty when signed out). */
  organisations: OrgSummary[];
  /** Id of the currently-selected organisation (persisted to localStorage). */
  selectedOrgId: string | null;
}

interface AuthContextValue extends AuthState {
  /** The currently-selected organisation, resolved from the list (or null). */
  selectedOrganisation: OrgSummary | null;
  /** Re-fetch /auth/me (after login, org switch, etc.). */
  refresh: () => Promise<void>;
  /** Re-fetch the organisation list and re-resolve the selected one. */
  refreshOrganisations: () => Promise<void>;
  /** Select an organisation locally (persisted); does not switch the session. */
  selectOrganisation: (merchantOrgId: string) => void;
  // ── passwordless helpers (stubs) ──
  requestCode: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string, inviteToken?: string) => Promise<void>;
  stepUp: (totp: string) => Promise<void>;
  logout: () => Promise<void>;
  switchOrg: (merchantOrgId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredOrgId(): string | null {
  try {
    return localStorage.getItem(SELECTED_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredOrgId(id: string | null): void {
  try {
    if (id) localStorage.setItem(SELECTED_ORG_STORAGE_KEY, id);
    else localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
  } catch {
    /* localStorage unavailable (private mode) — the selection just isn't persisted. */
  }
}

/** Pick which org to select: prefer an explicit choice, then the active org, then the first. */
function pickSelectedOrgId(
  orgs: OrgSummary[],
  preferredId: string | null,
  activeOrgId: string | null,
): string | null {
  const has = (id: string | null): boolean => id != null && orgs.some((o) => o.id === id);
  if (has(preferredId)) return preferredId;
  if (has(activeOrgId)) return activeOrgId;
  return orgs[0]?.id ?? null;
}

/** Fetch the user's organisations; returns [] when signed out or on error. */
async function fetchOrganisations(): Promise<OrgSummary[]> {
  try {
    const res = await api.get<OrgListResponse>('/org/list');
    // A malformed body must not throw downstream and sign the user out.
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

const SIGNED_OUT: AuthState = {
  initialised: true,
  me: null,
  organisations: [],
  selectedOrgId: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    initialised: false,
    me: null,
    organisations: [],
    selectedOrgId: null,
  });

  const loadMe = useCallback(async () => {
    try {
      const me = await api.get<AuthMe>('/auth/me');
      // Signed in — populate the organisation list and resolve the selection.
      const organisations = await fetchOrganisations();
      const selectedOrgId = pickSelectedOrgId(
        organisations,
        readStoredOrgId(),
        me.activeMerchantOrgId,
      );
      writeStoredOrgId(selectedOrgId);
      setState({ initialised: true, me, organisations, selectedOrgId });
    } catch (err) {
      // 401 (no session) is expected pre-login; 501 while auth is a stub. Any
      // failure lands as signed-out with an empty org list.
      if (err instanceof ApiClientError && (err.status === 401 || err.status === 501)) {
        setState(SIGNED_OUT);
        return;
      }
      setState(SIGNED_OUT);
    }
  }, []);

  const refreshOrganisations = useCallback(async () => {
    const organisations = await fetchOrganisations();
    setState((prev) => {
      // Keep the user's current selection if it's still present; otherwise fall
      // back to the stored one, then the active org, then the first.
      const selectedOrgId = pickSelectedOrgId(
        organisations,
        prev.selectedOrgId ?? readStoredOrgId(),
        prev.me?.activeMerchantOrgId ?? null,
      );
      writeStoredOrgId(selectedOrgId);
      return { ...prev, organisations, selectedOrgId };
    });
  }, []);

  const selectOrganisation = useCallback((merchantOrgId: string) => {
    writeStoredOrgId(merchantOrgId);
    setState((prev) => ({ ...prev, selectedOrgId: merchantOrgId }));
  }, []);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      selectedOrganisation: state.organisations.find((o) => o.id === state.selectedOrgId) ?? null,
      refresh: loadMe,
      refreshOrganisations,
      selectOrganisation,
      requestCode: async (email) => {
        await api.post('/auth/request-code', { email });
      },
      verifyCode: async (email, code, inviteToken) => {
        await api.post('/auth/verify-code', { email, code, inviteToken });
        await loadMe();
      },
      stepUp: async (totp) => {
        await api.post('/auth/step-up', { totp });
        await loadMe();
      },
      logout: async () => {
        await api.post('/auth/logout');
        writeStoredOrgId(null);
        setState(SIGNED_OUT);
      },
      switchOrg: async (merchantOrgId) => {
        await api.post('/org/switch', { merchantOrgId });
        // Remember the choice so the reload (and future sessions) re-select it.
        writeStoredOrgId(merchantOrgId);
        await loadMe();
      },
    }),
    [state, loadMe, refreshOrganisations, selectOrganisation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/** Like {@link useAuth} but returns null instead of throwing when no provider is mounted. */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}

/** The signed-in user's organisations (empty when signed out). */
export function useOrganisations(): OrgSummary[] {
  return useAuth().organisations;
}

/** The currently-selected organisation (or null). */
export function useSelectedOrganisation(): OrgSummary | null {
  return useAuth().selectedOrganisation;
}

/** The active per-org role (null if signed out / no active org). */
export function useRole(): Role | null {
  return useAuth().me?.activeRole ?? null;
}

/** The platform-admin role (gates /admin). */
export function usePlatformRole(): PlatformRole | null {
  return useAuth().me?.platformRole ?? null;
}

/** Convenience: does the active role satisfy one of `allowed`? */
export function useHasRole(...allowed: Role[]): boolean {
  const role = useRole();
  return role != null && allowed.includes(role);
}
